/**
 * tm/flow/FlowActions.tsx — 지금 단계 버튼 · 되돌리기 10초 (대시보드 · 업체별 관리 · /m 공용) — 2026-10-12 · 4차 시안
 *  내 차례: [✓ 완료] [+3일] [+7일] ([날짜]) [회신 대기 ▾] [⋯ 보류 · 중단 · 업무 열기] — 채운 버튼은 줄마다 하나(완료)
 *  회신 대기: [재촉 문자…] [↩ 회신 받음] [⋯]
 *  3번째 미루기부터 이유를 하나 고른다(업체 답을 기다림 → 회신 대기 · 업체가 안 하기로 함 → 중단)
 *  중단 = 이유 버튼 하나(기타는 직접 쓰기) — 브라우저 확인 창 없이, 바로 바뀌고 10초 되돌리기
 *  ⋯ 메뉴 2026-10-12 밤: 「업무 전체 완료」(남은 단계 모두 완료) · 「업무 지우기」(그 줄 안에서 한 번 더 · 휴지통 30일 · 10초 되돌리기)
 *  runMany — 여러 줄 한꺼번에(전체 보기 「골라서 정리」) · 되돌리기도 한꺼번에
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import * as api from '../api';
import type { FlowBody, FlowUndo } from '../api';
import type { Company, Task, TaskStep } from '../types';
import { ceoContact } from '../types';
import { C, T, R } from '../components/ui';
import { CLOSE_REASONS, addDays, judgeOf, kstToday, md, sortedSteps, stepEx } from './flowLogic';

export const GREEN_BTN = '#15803D';
export const TEAL = '#0A7D74';
export const TEAL_DK = '#0E5E58';
export const STOP_REASONS = ['비용 부담', '다른 곳에서 진행', '필요 없어짐', '연락 안 됨'];

/* ── 실행 + 되돌리기 ── */
type UndoItem = { taskId: string; undo: FlowUndo | null };   // undo null = 지운 업무(휴지통에서 되살림)
interface UndoState { items: UndoItem[]; text: string; key: number }
export function useFlow(onChanged: () => void | Promise<void>, onError: (m: string) => void, bottom = 20): {
  run: (task: Task, body: FlowBody, label?: string) => Promise<boolean>;
  runMany: (tasks: Task[], action: 'finish' | 'trash') => Promise<number>;
  undoNode: ReactNode;
} {
  const [u, setU] = useState<UndoState | null>(null);
  /** 한 건 — 성공하면 되돌릴 거리 · 실패면 null */
  const one = async (task: Task, body: FlowBody): Promise<{ item: UndoItem; message: string } | null> => {
    if (body.action === 'trash') {
      const r = await api.deleteTask(task.id);
      if (!r.success) throw new Error(r.error || '지우지 못했어요');
      return { item: { taskId: task.id, undo: null }, message: '업무를 지웠어요 · 휴지통 30일' };
    }
    const r = await api.taskFlow(task.id, body);
    if (!r.success || !r.data) throw new Error(r.error || '바꾸지 못했어요');
    return { item: { taskId: task.id, undo: r.data.undo }, message: r.data.message };
  };
  const run = async (task: Task, body: FlowBody, label?: string) => {
    try {
      const x = await one(task, body);
      if (!x) return false;
      setU({ items: [x.item], text: `${label ? label + ' · ' : ''}${x.message}`, key: Date.now() });
      await onChanged();
      return true;
    } catch (e: unknown) { onError(e instanceof Error ? e.message : '바꾸지 못했어요'); return false; }
  };
  const runMany = async (tasks: Task[], action: 'finish' | 'trash') => {
    const items: UndoItem[] = [];
    let fail = 0;
    for (const t of tasks) { try { const x = await one(t, { action }); if (x) items.push(x.item); } catch { fail += 1; } }
    if (items.length) setU({ items, text: `${items.length}건 ${action === 'trash' ? '지웠어요 · 휴지통 30일' : '전체 완료'}${fail ? ` · ${fail}건 못 함` : ''}`, key: Date.now() });
    else if (fail) onError(`${fail}건 하지 못했어요`);
    await onChanged();
    return items.length;
  };
  const undoNode = u ? <UndoToast key={u.key} text={u.text} onUndo={async () => {
    const cur = u; setU(null);
    let fail = 0;
    for (const it of cur.items) {
      try { const r = it.undo ? await api.taskRestore(it.taskId, it.undo) : await api.undeleteTask(it.taskId); if (!r.success) fail += 1; }
      catch { fail += 1; }
    }
    if (fail) onError(`${fail}건 되돌리지 못했어요`);
    await onChanged();
  }} onClose={() => setU(null)} bottom={bottom} /> : null;
  return { run, runMany, undoNode };
}

function UndoToast({ text, onUndo, onClose, bottom }: { text: string; onUndo: () => void; onClose: () => void; bottom: number }) {
  // 휴대폰엔 마우스가 없다 — 「마우스를 올리면 멈춤」은 마우스가 있는 화면에서만(2026-10-28 /m 업체 화면)
  const [hoverable] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: hover)').matches);
  const [left, setLeft] = useState(10000);
  const hover = useRef(false);
  useEffect(() => {
    const t = window.setInterval(() => { if (!hover.current) setLeft(l => l - 200); }, 200);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => { if (left <= 0) onClose(); }, [left, onClose]);
  return (
    <div role="status" onMouseEnter={() => { hover.current = true; }} onMouseLeave={() => { hover.current = false; }}
      style={{ position: 'fixed', left: 12, right: 12, width: 'fit-content', margin: '0 auto', bottom: `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`, zIndex: 3000, background: '#111827', color: '#fff', borderRadius: 12, padding: '10px 12px 10px 16px', display: 'flex', alignItems: 'center', gap: 12, boxSizing: 'border-box', boxShadow: '0 8px 24px rgba(0,0,0,.2)', overflow: 'hidden' }}>
      <span style={{ fontSize: T.md, lineHeight: 1.5 }}>{text}</span>
      <button onClick={onUndo} style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,.4)', background: 'transparent', color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>되돌리기</button>
      <span style={{ flexShrink: 0, fontSize: T.xs, color: 'rgba(255,255,255,.6)' }}>{Math.ceil(left / 1000)}초{hoverable ? ' · 마우스를 올리면 멈춤' : ''}</span>
      <span style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: `${left / 100}%`, background: '#60A5FA' }} />
    </div>
  );
}

/* ── 버튼 모양 ── */
const btn = (extra?: CSSProperties): CSSProperties => ({
  height: 32, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.text,
  fontSize: T.sm, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, ...extra,
});

/** 다시 신청 달 — 오늘에서 k달 뒤 첫 평일 · 이름 「다음 달 (11월)」 「12월」 */
export function retryMonth(today: string, k: number): { date: string; m: number; label: string } {
  const y0 = +today.slice(0, 4), m0 = +today.slice(5, 7) - 1 + k;
  const y = y0 + Math.floor(m0 / 12), m = (m0 % 12) + 1;
  const d1 = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const day = d1 === 6 ? 3 : d1 === 0 ? 2 : 1;
  const date = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return { date, m, label: k === 1 ? `다음 달 (${m}월)` : `${m}월` };
}
/** 「미승인」 → 「승인되지 않았어요」 (고객 · 팀장님 화면과 같은 말 · portalLogic.resultSentence) */
export function notSentence(word: string): string {
  return word === '미승인' ? '승인되지 않았어요' : word === '미통과' ? '통과하지 못했어요' : '선정되지 않았어요';
}

type Panel = null | 'wait' | 'stop' | 'more' | 'reason' | 'nudge' | 'date' | 'del' | 'close' | 'goto';

/** 다음 회차 준비 — 같은 업무를 새로(단계 이름 · 종류 그대로 · 기한 없음 · 첫 단계부터) · 이번 회차 업무는 기록으로 남김 */
export async function nextRound(task: Task): Promise<boolean> {
  const ss = sortedSteps(task);
  const r = await api.createTask({
    company_id: task.company_id, template_id: task.template_id, type: task.type, icon: task.icon, category_path: task.category_path, status: '진행',
    assigned_to: task.assigned_to || '',   // 다음 회차도 같은 담당
    steps: ss.map((x, i) => ({ name: x.name, type: x.type, status: i === 0 ? '진행' : '대기', due: null, note: x.note || '' })),
  });
  return !!r.success;
}

interface Props {
  task: Task; comp: Company; step: TaskStep | null; kind: 'mine' | 'wait';
  run: (task: Task, body: FlowBody, label?: string) => Promise<boolean>;
  fpPhone?: string; fpName?: string;
  showDates?: boolean; datePick?: boolean; onOpen?: () => void;
  /** 이미 끝낸 결과 단계에 결과만 고르기(승인 / 미승인) — 미루기 · 회신 대기 버튼 없이 */
  resultOnly?: boolean;
  /** 버튼 묶음 아래 펼침 패널을 그릴 곳(없으면 버튼 바로 아래) */
  panelIndent?: number;
  compact?: boolean;
  /** 휴대폰 크기 버튼 */
  big?: boolean;
  /** ⋯ 메뉴만(업무 전체 완료 · 보류 · 중단 · 지우기) — 진행 버튼이 따로 있는 줄(빠진 입력 · 2026-10-27) */
  moreOnly?: boolean;
}

/** 버튼 묶음 + 아래 펼침 패널. 반환: { bar, panel } 을 FlowItem 이 배치 */
export function useFlowActions(p: Props): { bar: ReactNode; panel: ReactNode } {
  const { task, comp, step, kind, run } = p;
  // 휴대폰(/m) — 누르는 곳 44px · 글자 15px
  const B = (extra?: CSSProperties): CSSProperties => btn({ ...(p.big ? { height: 44, fontSize: 15, padding: '0 14px' } : {}), ...extra });
  const [panel, setPanel] = useState<Panel>(null);
  const [pendingDays, setPendingDays] = useState(3);
  const [who, setWho] = useState<string[]>(['업체', 'FP']);
  const [q, setQ] = useState('');
  const [other, setOther] = useState('');
  const [date, setDate] = useState('');
  const ex = stepEx(step);
  const postponed = Number(ex.postponed) || 0;
  const label = comp.name;
  const today = kstToday();
  const due0 = step && step.due ? String(step.due).slice(0, 10) : '';
  const base = due0 && due0 > today ? due0 : today;   // 서버 stepFlow 와 같은 기준 — 원래 기한과 오늘 중 늦은 날
  const toggle = (x: Panel) => setPanel(cur => (cur === x ? null : x));
  // ⋯ 메뉴는 화면 기준(fixed)으로 띄운다 — 업무 카드가 overflow:hidden 이라 아래로 펼친 메뉴가 잘려
  //   업체별 · FP별 관리 업무 탭에서 「보류 · 중단(이유 고르기)」이 안 보였다(2026-10-18 지도사 「업무 중단 사유 선택 여기도 넣어야겠다」)
  const moreRef = useRef<HTMLButtonElement>(null);
  const [morePos, setMorePos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  const openMore = () => {
    const r = moreRef.current ? moreRef.current.getBoundingClientRect() : null;
    if (r) {
      const W = 260, H = 340;
      const left = Math.max(8, Math.min(window.innerWidth - W - 8, r.right - W));
      const below = window.innerHeight - r.bottom;
      setMorePos(below < H && r.top > below ? { left, bottom: window.innerHeight - r.top + 4 } : { left, top: r.bottom + 4 });
    }
    toggle('more');
  };
  useEffect(() => {
    if (panel !== 'more') return undefined;
    const close = (e: Event) => {
      const t = e.target as Node | null;
      if (e.type === 'mousedown' && t && (moreRef.current?.contains(t) || (t as Element).closest?.('[data-flow-more]'))) return;
      setPanel(null);
    };
    window.addEventListener('scroll', close, true); window.addEventListener('resize', close); window.addEventListener('mousedown', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); window.removeEventListener('mousedown', close); };
  }, [panel]);
  const judge = step ? judgeOf(step) : null;   // 심사 단계면 「✓ 통과」 「미통과」 두 버튼(선정 · 승인도 같은 꼴)
  const [closeOther, setCloseOther] = useState('');
  const [gotAt, setGotAt] = useState(today);         // 결과 받은 날(기본 오늘)
  const [retryDate, setRetryDate] = useState('');    // 다시 신청 — 날짜를 직접 고를 때
  const noWord = judge ? judge.no : '미선정';
  const close = (reason: string) => { if (!step) return; setPanel(null); setCloseOther(''); void run(task, { action: 'close', stepId: step.id, result_word: noWord, reason, result_at: gotAt }, label); };
  /** 다시 신청(2026-10-18) — 같은 업무에 「다시 신청 · 다시 신청 결과 확인」 · exact = 날짜를 직접 골랐나(아니면 고객 화면엔 「11월 예정」) */
  const retry = (date: string, exact: boolean) => { if (!step || !date) return; setPanel(null); setRetryDate(''); void run(task, { action: 'retry', stepId: step.id, date, exact, result_word: noWord, result_at: gotAt }, label); };

  const postpone = (days: number) => {
    if (!step) return;
    if (postponed >= 2) { setPendingDays(days); setPanel('reason'); return; }
    void run(task, { action: 'postpone', stepId: step.id, days }, label);
  };
  const stop = (reason: string) => { if (!reason.trim()) return; setPanel(null); void run(task, { action: 'stop', reason: reason.trim() }, label); };

  const more = panel === 'more' && (
    <div data-flow-more="" style={{ position: 'fixed', left: morePos ? morePos.left : 8, ...(morePos && morePos.bottom !== undefined ? { bottom: morePos.bottom } : { top: morePos && morePos.top !== undefined ? morePos.top : 8 }), zIndex: 1000, width: 260, maxHeight: 'calc(100vh - 16px)', overflowY: 'auto', background: C.white, border: `1px solid ${C.border}`, borderRadius: R.md, boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden' }}>
      {[
        { k: 'finish', t: '업무 전체 완료', s: '남은 단계를 모두 완료로 · 되돌리기 가능', icon: '✓', danger: false },
        ...((task.steps || []).length > 1 ? [{ k: 'goto', t: '단계 이동', s: '앞뒤 어느 단계로든 · 되돌리기 가능', icon: '⇄', danger: false }] : []),   // 2026-10-28 지도사 「업무 종료 · 단계 이동 등도」
        ...(step && judge ? [{ k: 'close', t: `결과 ${noWord}`, s: '다시 신청하거나 이번 회차를 마감해요', icon: '↻', danger: false }] : []),   // 결과 단계에만(「필요자료 수집」에 「결과 미선정」이 보였음 · 반쪽 원 기호 ✕ · 2026-10-27)
        { k: 'hold', t: '보류 — 잠시 멈춤', s: '날짜가 멈춤 · 언제든 다시 시작', icon: 'Ⅱ', danger: false },
        { k: 'stop', t: '중단 — 업체가 안 하기로 함', s: '이유 한 번 누르면 끝 · 되돌리기 가능', icon: '✕', danger: true },
        ...(p.onOpen ? [{ k: 'open', t: '업무 열기', s: '업체별 관리 › 업무 탭', icon: '↗', danger: false }] : []),
        { k: 'del', t: '업무 지우기', s: '잘못 만든 업무 · 휴지통에 30일', icon: '🗑', danger: true },
      ].map(m => (
        <button key={m.k} onClick={() => {
          if (m.k === 'hold') { setPanel(null); void run(task, { action: 'hold' }, label); }
          else if (m.k === 'finish') { setPanel(null); void run(task, { action: 'finish' }, label); }
          else if (m.k === 'close') setPanel('close');
          else if (m.k === 'goto') setPanel('goto');
          else if (m.k === 'stop') setPanel('stop');
          else if (m.k === 'del') setPanel('del');
          else { setPanel(null); if (p.onOpen) p.onOpen(); }
        }}
          style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: `1px solid ${C.border}`, background: m.danger ? '#FEF2F2' : C.white, cursor: 'pointer' }}>
          <span style={{ width: 14, fontSize: T.sm, color: m.danger ? C.red : C.sub }}>{m.icon}</span>
          <span><span style={{ display: 'block', fontSize: T.md, fontWeight: 700, color: m.danger ? C.red : C.text }}>{m.t}</span><span style={{ fontSize: T.xs, color: C.muted }}>{m.s}</span></span>
        </button>
      ))}
    </div>
  );

  const moreBtn = (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button ref={moreRef} aria-label="더 보기" title="업무 전체 완료 · 보류 · 중단(이유) · 지우기" onClick={openMore} style={B({ width: 32, padding: 0, justifyContent: 'center', borderColor: panel === 'more' ? C.accent : C.border })}>⋯</button>
      {more}
    </span>
  );

  const bar = p.moreOnly ? <span style={{ display: 'inline-flex' }} onClick={e => e.stopPropagation()}>{moreBtn}</span> : kind === 'mine' && step ? (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: p.compact || p.big ? 'wrap' : 'nowrap' }} onClick={e => e.stopPropagation()}>
      {judge ? (<span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {/* 「승인 · 미승인」만 있으면 무엇을 고르는지 안 읽힘(2026-10-27 지도사 「승인, 미승인은 뭐지? 학습 없이 쓸 수 있도록」) — 앞에 「결과」 */}
        <span style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginRight: 6, whiteSpace: 'nowrap' }}>결과</span>
        <button title={`${step.name} — ${judge.yes}이 났으면 눌러요 · 다음 단계로`} onClick={() => void run(task, { action: 'done', stepId: step.id, result_word: judge.yes }, label)} style={B({ background: GREEN_BTN, borderColor: GREEN_BTN, color: '#fff', borderRadius: `${R.sm}px 0 0 ${R.sm}px` })}>✓ {judge.yes}</button>
        <button title={`${judge.no}이면 눌러요 · 다시 신청할지 여기서 마감할지 골라요`} onClick={() => toggle('close')} style={B({ borderRadius: `0 ${R.sm}px ${R.sm}px 0`, borderLeft: 'none', color: '#334155', borderColor: panel === 'close' ? '#334155' : C.border })}>{judge.no}</button>
      </span>) : (
        <button onClick={() => void run(task, { action: 'done', stepId: step.id }, label)} style={B({ background: GREEN_BTN, borderColor: GREEN_BTN, color: '#fff' })}>✓ 완료{p.showDates ? ' → 다음 단계' : ''}</button>
      )}
      {!p.resultOnly && <span style={{ display: 'inline-flex' }}>
        <button onClick={() => postpone(3)} style={B({ borderRadius: `${R.sm}px 0 0 ${R.sm}px` })}>+3일{p.showDates ? ` (${md(addDays(base, 3)).replace(/\(.\)/, '')})` : ''}</button>
        <button onClick={() => postpone(7)} style={B({ borderRadius: `0 ${R.sm}px ${R.sm}px 0`, borderLeft: 'none' })}>+7일{p.showDates ? ` (${md(addDays(base, 7)).replace(/\(.\)/, '')})` : ''}</button>
      </span>}
      {p.datePick && !p.resultOnly && <button onClick={() => toggle('date')} style={B()}>📅 날짜</button>}
      {/* 흰 바탕 — 청록 채움은 상태 배지처럼 보였음(2026-10-12 검수) */}
      {!p.resultOnly && <button onClick={() => toggle('wait')} style={B({ borderColor: panel === 'wait' ? TEAL : C.border, color: TEAL })}>회신 대기로 ▾</button>}
      {moreBtn}
    </div>
  ) : kind === 'wait' ? (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      {step && step.status === '회신대기' && <button onClick={() => toggle('nudge')} style={B()}>재촉 문자…</button>}
      <button onClick={() => void (step && step.status === '회신대기' ? run(task, { action: 'reply', stepId: step.id }, label) : run(task, { action: 'restart' }, label))}
        style={B({ color: TEAL, borderColor: TEAL })}>↩ 회신 받음</button>
      {moreBtn}
    </div>
  ) : null;

  const box = (children: ReactNode, tone: 'gray' | 'red' | 'teal' = 'gray') => (
    <div onClick={e => e.stopPropagation()} style={{ margin: `0 14px 10px ${p.panelIndent ?? 14}px`, padding: '10px 12px', borderRadius: R.md, border: `1px solid ${tone === 'red' ? '#FCA5A5' : tone === 'teal' ? '#B6E6DF' : C.border}`, background: tone === 'red' ? '#FEF2F2' : tone === 'teal' ? '#F2FBFA' : C.bgAlt }}>{children}</div>
  );
  const cancel = <button onClick={() => setPanel(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: C.accent, fontSize: T.sm, cursor: 'pointer' }}>취소</button>;

  let panelNode: ReactNode = null;
  if (panel === 'wait' && step) {
    panelNode = box(<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: T.sm, color: TEAL }}>누구 답을 기다리나요</b>
        {['업체', 'FP'].map(w => (
          <label key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: T.sm, cursor: 'pointer' }}>
            <input type="checkbox" checked={who.includes(w)} onChange={() => setWho(c => (c.includes(w) ? c.filter(x => x !== w) : [...c, w]))} />{w}
          </label>
        ))}
        <span style={{ fontSize: T.xs, color: C.muted }}>둘 중 누구든 먼저 답하면 끝</span>
        {cancel}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="물어본 것(선택) — 고객 페이지 · FP 포털에 보여요" style={{ flex: 1, minWidth: 0, height: 32, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
        <button disabled={!who.length} onClick={() => { setPanel(null); void run(task, { action: 'wait', stepId: step.id, wait_for: who, question: q }, label); }} style={B({ background: TEAL, borderColor: TEAL, color: '#fff', opacity: who.length ? 1 : 0.5 })}>회신 대기로</button>
      </div>
    </>, 'teal');
  } else if (panel === 'goto') {
    // 단계 이동(2026-10-28) — 고른 단계가 지금 단계 · 앞은 완료 · 뒤는 대기(완료였으면 풀림) · 서버 stepFlow goto
    const ss = sortedSteps(task);
    const curId = step ? step.id : '';
    panelNode = box(<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: T.sm }}>⇄ 단계 이동</b>
        <span style={{ fontSize: T.sm, color: C.sub }}>옮길 단계를 누르세요 · 앞 단계는 완료, 뒤 단계는 대기로</span>
        {cancel}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {ss.map((x, i) => {
          const cur = x.id === curId;
          return (
            <button key={x.id} disabled={cur} onClick={() => { setPanel(null); void run(task, { action: 'goto', stepId: x.id }, label); }}
              style={B({ ...(cur ? { background: C.accent, borderColor: C.accent, color: '#fff', cursor: 'default' } : x.status === '완료' ? { color: C.muted } : {}) })}>
              {i + 1}. {x.name}{cur ? ' · 지금' : x.status === '완료' ? ' ✓' : ''}
            </button>
          );
        })}
      </div>
    </>);
  } else if (panel === 'stop') {
    panelNode = box(<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: T.sm, color: C.red }}>✕ 업무 중단</b>
        <span style={{ fontSize: T.sm, color: C.sub }}>업체가 안 하기로 했나요? 이유를 누르면 바로 중단돼요</span>
        {cancel}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {STOP_REASONS.map(r => <button key={r} onClick={() => stop(r)} style={B()}>{r}</button>)}
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <input value={other} onChange={e => setOther(e.target.value)} placeholder="기타 (직접 쓰기)" style={{ width: 150, height: 32, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
          {other.trim() && <button onClick={() => stop(other)} style={B()}>중단</button>}
        </span>
      </div>
      <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6 }}>남은 단계는 목록에서 빠지고 기록은 남아요 · FP 포털엔 「중단」 · 언제든 「다시 진행」</div>
    </>, 'red');
  } else if (panel === 'close' && step) {
    // 결과가 「미○○」(2026-10-18 지도사 「1차 결과 탈락 · 다음 달 재신청할 수도 · 오재인이 빠짐없이 입력하게」)
    //  둘 중 하나는 꼭 고른다: 다시 신청(다음 달 · 그다음 달 · 날짜) / 이번엔 여기서 마감(이유) · 결과 받은 날도 함께
    const months = [1, 2].map(k => retryMonth(today, k));
    const row: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 10 };
    const lab: CSSProperties = { width: 92, flexShrink: 0, fontSize: T.sm, fontWeight: 700, color: C.text };
    panelNode = box(<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: T.sm, color: '#334155' }}>「{step.name}」 {noWord}</b>
        <span style={{ fontSize: T.sm, color: C.sub }}>다음은 어떻게 하나요?</span>
        {cancel}
      </div>
      <div style={row}>
        <span style={lab}>결과 받은 날</span>
        <input type="date" value={gotAt} max={today} onChange={e => setGotAt(e.target.value || today)} style={{ height: 32, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm }} />
      </div>
      <div style={row}>
        <span style={lab}>다시 신청</span>
        {months.map(m => <button key={m.date} onClick={() => retry(m.date, false)} style={B({ color: C.accent, borderColor: C.accent })}>{m.label}</button>)}
        <input type="date" value={retryDate} min={gotAt} onChange={e => setRetryDate(e.target.value)} style={{ height: 32, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm }} />
        {retryDate && <button onClick={() => retry(retryDate, true)} style={B({ background: C.accent, borderColor: C.accent, color: '#fff' })}>{md(retryDate)} 다시 신청</button>}
      </div>
      <div style={row}>
        <span style={lab}>여기서 마감</span>
        {CLOSE_REASONS.map(r => <button key={r} onClick={() => close(r)} style={B()}>{r}</button>)}
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <input value={closeOther} onChange={e => setCloseOther(e.target.value)} placeholder="기타 (직접 쓰기)" style={{ width: 150, height: 32, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
          {closeOther.trim() && <button onClick={() => close(closeOther.trim())} style={B()}>마감</button>}
        </span>
        <button onClick={() => close('')} style={B({ color: C.sub })}>이유 없이 마감</button>
      </div>
      <div style={{ fontSize: T.xs, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
        다시 신청: 이 업무에 「다시 신청」과 「다시 신청 결과 확인」 단계가 붙고, 그날 내 차례에 떠요. 대표님과 팀장님 화면엔 「이번엔 {notSentence(noWord)}. {months[0].m}월에 다시 신청해요」처럼 보여요.<br />
        여기서 마감: 끝난 일로 옮겨지고 「이번 회차는 선정되지 않았어요」로 보여요. 이유는 지도사 화면에만 남아요. 문자는 어느 쪽도 나가지 않고, 10초 안에 되돌릴 수 있어요.
      </div>
    </>);
  } else if (panel === 'del') {
    // 확인 창 없이 그 줄 안에서 한 번 더(무엇을 지우는지 이름으로) — 지운 뒤 10초 되돌리기 · 휴지통 30일
    panelNode = box(<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: T.sm, color: C.red }}>「{comp.name} · {task.type}」 업무를 지울까요?</b>
        <span style={{ fontSize: T.sm, color: C.sub }}>단계 {(task.steps || []).length}개도 함께 · 휴지통에 30일 보관</span>
        {cancel}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={() => { setPanel(null); void run(task, { action: 'trash' }, label); }} style={B({ background: C.red, borderColor: C.red, color: '#fff' })}>지우기</button>
        <button onClick={() => setPanel(null)} style={B()}>그대로 두기</button>
        <span style={{ fontSize: T.xs, color: C.muted, alignSelf: 'center' }}>끝난 업무라면 지우지 말고 ⋯ › 「업무 전체 완료」가 기록이 남아요</span>
      </div>
    </>, 'red');
  } else if (panel === 'reason' && step) {
    const to = md(addDays(base, pendingDays));
    panelNode = box(<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: T.sm }}>{postponed + 1}번째 미루기(+{pendingDays}일 → {to}) · 이유를 하나 고르세요</b>{cancel}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {['내 일정이 밀림', '자료 준비 중'].map(r => (
          <button key={r} onClick={() => { setPanel(null); void run(task, { action: 'postpone', stepId: step.id, days: pendingDays, reason: r }, label); }} style={B()}>{r}</button>
        ))}
        <button onClick={() => { setPanel('wait'); }} style={B()}>업체·FP 답을 기다림 → 회신 대기로</button>
        {/* 멈추는 동작은 맨 끝 · 따로 띄우고 글자만 빨강(가장 눈에 띄거나 기본처럼 보이지 않게) */}
        <button onClick={() => setPanel('stop')} style={B({ marginLeft: 10, color: C.red })}>업체가 안 하기로 함 → 중단</button>
      </div>
    </>);
  } else if (panel === 'date' && step) {
    panelNode = box(<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <b style={{ fontSize: T.sm }}>새 기한</b>
      <input type="date" value={date} min={today} onChange={e => setDate(e.target.value)} style={{ height: 32, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm }} />
      <button disabled={!date} onClick={() => { setPanel(null); void run(task, { action: 'postpone', stepId: step.id, date }, label); }} style={B({ background: '#111827', color: '#fff', borderColor: '#111827', opacity: date ? 1 : 0.5 })}>{date ? `${md(date)}로 미루기` : '날짜를 고르세요'}</button>
      <span style={{ fontSize: T.xs, color: C.muted }}>뒤 단계도 같은 날수만큼 · 공고 마감 단계는 그대로</span>
      {cancel}
    </div>);
  } else if (panel === 'nudge' && step) {
    panelNode = <NudgeBox task={task} comp={comp} step={step} fpPhone={p.fpPhone} fpName={p.fpName} indent={p.panelIndent ?? 14}
      onSent={() => { setPanel(null); void run(task, { action: 'nudge', stepId: step.id }, label); }} onCancel={() => setPanel(null)} />;
  }
  return { bar, panel: panelNode };
}

/** 재촉 문자 — 받는 사람(대표 · FP)과 글을 보고 보낸다. 자동 독촉은 하지 않음(지도사 결정 전) */
function NudgeBox({ task, comp, step, fpPhone, fpName, indent, onSent, onCancel }: { task: Task; comp: Company; step: TaskStep; fpPhone?: string; fpName?: string; indent: number; onSent: () => void; onCancel: () => void }) {
  const ex = stepEx(step);
  const w = ex.wait_for && ex.wait_for.length ? ex.wait_for : ['업체', 'FP'];
  const targets = [
    ...(w.includes('업체') && ceoContact(comp).phone ? [{ k: '대표', name: `${ceoContact(comp).name || '대표'} 대표님`, phone: ceoContact(comp).phone }] : []),   // 실제 대표가 있으면 그 사람(2026-10-18)
    ...(w.includes('FP') && fpPhone ? [{ k: 'FP', name: `${fpName || ''} FP님`, phone: fpPhone }] : []),
  ];
  const [sel, setSel] = useState<string[]>(targets.map(t => t.k));
  const [text, setText] = useState(`[임지도사] ${comp.name} ${task.type} — ${ex.wait_question || step.name} 관련 회신 부탁드립니다. 문자나 카카오톡으로 남겨 주시면 됩니다.`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const send = async () => {
    setBusy(true); setErr('');
    try {
      for (const t of targets.filter(x => sel.includes(x.k))) {
        const r = await api.sendSms({ to: t.phone, message: text, context: 'tm_nudge', company_id: task.company_id, reference_id: task.id });
        if (!r.success) throw new Error(r.error || '보내지 못했어요');
      }
      onSent();
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : '보내지 못했어요'); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={e => e.stopPropagation()} style={{ margin: `0 14px 10px ${indent}px`, padding: '10px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: T.sm }}>재촉 문자</b>
        {targets.length === 0 && <span style={{ fontSize: T.sm, color: C.red }}>받을 번호가 없어요(대표 연락처 · FP 연락처)</span>}
        {targets.map(t => (
          <label key={t.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: T.sm, cursor: 'pointer' }}>
            <input type="checkbox" checked={sel.includes(t.k)} onChange={() => setSel(c => (c.includes(t.k) ? c.filter(x => x !== t.k) : [...c, t.k]))} />{t.name}
          </label>
        ))}
        <button onClick={onCancel} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: C.accent, fontSize: T.sm, cursor: 'pointer' }}>취소</button>
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: 8, borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.6, resize: 'vertical' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <button disabled={busy || !sel.length || !text.trim()} onClick={() => void send()} style={btn({ background: '#111827', color: '#fff', borderColor: '#111827', opacity: busy || !sel.length ? 0.5 : 1 })}>{busy ? '보내는 중' : `${sel.length}명에게 보내기`}</button>
        {err && <span style={{ fontSize: T.sm, color: C.red }}>{err}</span>}
      </div>
    </div>
  );
}

/** 한 줄 = 머리(배지 · 이름 · 설명) + 버튼 묶음 + 펼침 패널 */
export function FlowItem({ head, onClick, last, ...p }: Props & { head: ReactNode; onClick?: () => void; last?: boolean }) {
  const { bar, panel } = useFlowActions(p);
  return (
    <div style={{ borderBottom: last ? 'none' : `1px solid ${C.border}` }}>
      <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: onClick ? 'pointer' : 'default', flexWrap: p.compact ? 'wrap' : 'nowrap' }}>
        {head}{bar}
      </div>
      {panel}
    </div>
  );
}
